import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Lock } from "lucide-react";
import { COMPANY } from "../../lib/api";

const NAV = [
  { label: "Αρχική", to: "/" },
  { label: "Υπηρεσίες", to: "/services" },
  { label: "Εταιρεία", to: "/about" },
  { label: "Καριέρα", to: "/careers" },
  { label: "Επικοινωνία", to: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close menu and scroll-top on route change
  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled || location.pathname !== "/"
          ? "backdrop-blur-xl bg-[#050505]/85 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="header-logo-link"
          >
            <img
              src={COMPANY.logo}
              alt="Technological Horizons"
              className="h-14 sm:h-16 w-auto"
              style={{ imageRendering: "auto" }}
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-heading text-base font-semibold tracking-tight text-white">
                Technological Horizons
              </span>
              <span className="font-mono-th text-[10px] tracking-[0.25em] uppercase text-[#A1A1AA]">
                Techorizons.gr
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.to.replace("/", "") || "home"}-link`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm transition-colors relative group ${
                    isActive ? "text-white" : "text-[#A1A1AA] hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {n.label}
                    <span
                      className={`absolute left-4 right-4 bottom-1 h-px bg-[#E63946] transition-transform origin-left ${
                        isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${COMPANY.phone}`}
              data-testid="header-phone-link"
              className="font-mono-th text-xs text-[#A1A1AA] hover:text-white transition-colors"
            >
              {COMPANY.phone}
            </a>
            <Link
              to="/admin/login"
              data-testid="header-admin-login-btn"
              title="Σύνδεση Διαχειριστή"
              className="w-9 h-9 flex items-center justify-center border border-[#27272A] text-[#A1A1AA] hover:text-[#E63946] hover:border-[#E63946] transition-colors"
            >
              <Lock size={14} />
            </Link>
            <Link
              to="/contact"
              data-testid="header-cta-button"
              className="btn-primary px-5 py-2.5 text-sm font-medium"
            >
              Επικοινωνία
            </Link>
            <a
              href="https://techorizonsportal.gr/login"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-portal-login-btn"
              className="btn-outline px-5 py-2.5 text-sm font-medium"
            >
              Portal Login
            </a>
          </div>

          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#050505] border-t border-white/10">
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className="px-3 py-3 text-[#FAFAFA] border-b border-white/5"
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="btn-primary mt-3 px-5 py-3 text-sm text-center"
            >
              Επικοινωνία
            </Link>
            <a
              href="https://techorizonsportal.gr/login"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-portal-login-link"
              className="mt-2 btn-outline px-5 py-3 text-sm text-center"
            >
              Portal Login
            </a>
            <Link
              to="/admin/login"
              data-testid="mobile-admin-login-link"
              className="mt-2 px-3 py-3 text-sm text-[#A1A1AA] text-center border border-[#27272A] inline-flex items-center justify-center gap-2"
            >
              <Lock size={14} /> Σύνδεση Διαχειριστή
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
